import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ActivityCharts, KpiStrip } from "@/components/reports/RecruiterActivityCharts";
import { MeetingsInterviewsTable } from "@/components/reports/MeetingsInterviewsTable";

const PERIODS = [["year", "This Year"], ["quarter", "This Quarter"], ["month", "This Month"], ["all", "All Time"]];
const COLS = [["booked", "Booked"], ["rescheduled", "Rescheduled"], ["cancelled", "Cancelled"], ["no_show", "No Show"], ["upcoming", "Upcoming"]];
const ROLE_CLS = { recruiting_manager: "bg-indigo-50 text-indigo-700", internal_recruiter: "bg-sky-50 text-sky-700",
  external_recruiter: "bg-amber-50 text-amber-700", referral_partner: "bg-emerald-50 text-emerald-700" };

function BookingTable({ data }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
      <p className="px-4 pt-4 font-semibold text-slate-800">Meetings booked</p>
      <table className="w-full text-sm mt-2" data-testid="bar-table">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
          <tr><th className="text-left px-4 py-2.5">Recruiter</th>{COLS.map(([k, l]) => <th key={k} className="text-right px-4 py-2.5">{l}</th>)}</tr>
        </thead>
        <tbody className="divide-y">
          {data.items.length === 0 ? (
            <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400" data-testid="bar-empty">No recruiters in your scope.</td></tr>
          ) : data.items.map((r) => (
            <tr key={r.user_id} data-testid={`bar-row-${r.user_id}`}>
              <td className="px-4 py-2.5"><p className="font-medium text-slate-800">{r.name} <span className={`ml-1 whitespace-nowrap text-[10px] font-medium rounded px-1.5 py-0.5 ${ROLE_CLS[r.role] || "bg-slate-100 text-slate-600"}`} data-testid={`bar-role-${r.user_id}`}>{r.role_label}</span>{r.inactive && <span className="ml-1 text-[10px] text-slate-400">(inactive)</span>}</p><p className="text-xs text-slate-400">{r.email}</p></td>
              {COLS.map(([k]) => <td key={k} className={`px-4 py-2.5 text-right tabular-nums ${k === "no_show" && r[k] ? "text-rose-600 font-medium" : ""}`} data-testid={`bar-${k}-${r.user_id}`}>{r[k]}</td>)}
            </tr>
          ))}
        </tbody>
        {data.items.length > 0 && (
          <tfoot className="bg-slate-50 font-semibold text-slate-700">
            <tr><td className="px-4 py-2.5">Total</td>{COLS.map(([k]) => <td key={k} className="px-4 py-2.5 text-right tabular-nums" data-testid={`bar-total-${k}`}>{data.totals[k] || 0}</td>)}</tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

// Recruiting → Recruiter Activity: compares every recruiter in scope. Org-scoped for Group Admins.
export default function BookerActivityReport() {
  const [period, setPeriod] = useState("year");
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/reports/recruiter-activity", { params: { period } }).then((r) => setData(r.data))
      .catch((e) => { toast.error(apiError(e)); setData({ items: [], totals: {} }); });
  }, [period]);
  return (
    <div data-testid="booker-activity-report" className="space-y-5">
      <PageHeader title="Recruiter Activity" description="Compare Recruiting Managers, Internal and External Recruiters and Referral Partners in your group."
        actions={<div className="flex items-center gap-2"><Link to="/reports/recruiter-activity/reconciliation" className="text-xs font-medium text-brand hover:underline" data-testid="ra-reconciliation-link">Count reconciliation</Link><Select value={period} onValueChange={setPeriod}><SelectTrigger className="w-40 bg-white" data-testid="ra-period"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`ra-period-${k}`}>{l}</SelectItem>)}</SelectContent></Select></div>} />
      {data === null ? <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> : (
        <>
          <KpiStrip totals={data.totals} />
          {data.items.length > 0 && <ActivityCharts items={data.items} />}
          <MeetingsInterviewsTable period={period} />
          {data.items.some((r) => !r.on_team) && <p className="text-xs text-slate-500" data-testid="ra-not-on-team-note">People marked <b>Not on recruiting team</b> recorded recruiting activity but aren't on a recruiting list. Add them in <b>Recruiting → My Team</b> (for example as a Recruiting Manager) so they can also be assigned to candidates. Their other roles, such as Group Admin, are kept.</p>}
          <p className="text-[11px] text-slate-400">Emails = sent from Top-Recruit · Calls = call activities logged on people · Interviews = completed meetings, credited to the host · Background checks, career conversions and referred candidates' interviews are credited to the candidate's assigned recruiter and referring partner · Meetings &amp; Interviews counts each meeting once; see definitions under the table.</p>
        </>
      )}
    </div>
  );
}
